import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "wage-excel-migration-dialog" }
const _hoisted_2 = { class: "wage-excel-migration-dialog__header" }
const _hoisted_3 = { class: "header-icon" }
const _hoisted_4 = { class: "header-badge" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "wage-excel-migration-dialog__body" }
const _hoisted_7 = { class: "migration-actions" }
const _hoisted_8 = { class: "migration-upload-label" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = {
  key: 0,
  class: "migration-file-name"
}
const _hoisted_11 = {
  key: 0,
  class: "migration-summary"
}
const _hoisted_12 = { class: "ok" }
const _hoisted_13 = {
  key: 0,
  class: "warn"
}
const _hoisted_14 = {
  key: 1,
  class: "err"
}
const _hoisted_15 = {
  key: 1,
  class: "migration-register-option"
}
const _hoisted_16 = {
  key: 2,
  class: "migration-preview-wrap"
}
const _hoisted_17 = { class: "migration-preview-table" }
const _hoisted_18 = { class: "num" }
const _hoisted_19 = { class: "msg-cell" }
const _hoisted_20 = { class: "wage-excel-migration-dialog__footer" }

import { computed, ref, watch } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { commonWageApi, homeScheduleApi } from '@/services/api';

export type WageMigrationType = 'home' | 'facility';

interface MigrationPreviewRow {
  rowNum: number;
  name: string;
  birth?: string | null;
  job?: string;
  status: 'ok' | 'error' | 'missing_employee';
  errors?: string[];
  warnings?: string[];
  preview?: Record<string, unknown>;
}

interface MigrationSummary {
  total: number;
  valid: number;
  missingEmployees: number;
  errors: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WageExcelMigrationModal',
  props: {
    modelValue: { type: Boolean },
    wageType: {},
    mCode: {},
    yearMonth: {},
    bizGubun: {},
    payDate: {}
  },
  emits: ["update:modelValue", "imported"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFileName = ref('');
const loading = ref(false);
const importing = ref(false);
const loadingText = ref('검증 중...');
const previewRows = ref<MigrationPreviewRow[]>([]);
const summary = ref<MigrationSummary | null>(null);
const registerMissingEmployees = ref(true);

const wageTypeLabel = computed(() => (props.wageType === 'home' ? '방문요양' : '요양시설'));

const api = computed(() => (props.wageType === 'home' ? homeScheduleApi : commonWageApi));

const importableRows = computed(() =>
  previewRows.value.filter((r) => {
    if (r.status === 'ok') return true;
    if (r.status === 'missing_employee' && registerMissingEmployees.value) return true;
    return false;
  })
);

const canImport = computed(() => importableRows.value.length > 0);

const importButtonLabel = computed(() => {
  const n = importableRows.value.length;
  if (!n) return '업로드';
  return `${n}건 업로드`;
});

const resetState = () => {
  selectedFileName.value = '';
  previewRows.value = [];
  summary.value = null;
  registerMissingEmployees.value = true;
  if (fileInputRef.value) fileInputRef.value.value = '';
};

watch(
  () => props.modelValue,
  (open) => {
    if (!open) resetState();
  }
);

const close = () => {
  emit('update:modelValue', false);
};

const downloadTemplate = async () => {
  try {
    loading.value = true;
    loadingText.value = '양식 다운로드 중...';
    const response = await api.value.downloadMigrationTemplate();
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `임금마이그레이션_${props.wageType === 'home' ? '방문요양' : '요양시설'}_양식.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`양식 다운로드 실패: ${message}`);
  } finally {
    loading.value = false;
  }
};

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  selectedFileName.value = file.name;
  previewRows.value = [];
  summary.value = null;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('mCode', props.mCode);
  formData.append('yearMonth', props.yearMonth);
  formData.append('bizGubun', props.bizGubun || '');

  try {
    loading.value = true;
    loadingText.value = '엑셀 검증 중...';
    const response = await api.value.validateMigrationExcel(formData);
    if (!response.data?.success) {
      alert(`검증 실패: ${response.data?.message || '알 수 없는 오류'}`);
      return;
    }
    previewRows.value = response.data.data?.rows || [];
    summary.value = response.data.data?.summary || null;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`엑셀 검증 실패: ${message}${error.response?.status ? ` (${error.response.status})` : ''}`);
  } finally {
    loading.value = false;
  }
};

const runImport = async () => {
  if (!canImport.value) return;

  const okCount = importableRows.value.length;
  if (!confirm(`${okCount}건의 임금 데이터를 업로드합니다. 계속하시겠습니까?`)) return;

  try {
    importing.value = true;
    loading.value = true;
    loadingText.value = '업로드 중...';

    const payload: Record<string, unknown> = {
      mCode: props.mCode,
      yearMonth: props.yearMonth,
      bizGubun: props.bizGubun || '',
      rows: importableRows.value,
      registerMissingEmployees: registerMissingEmployees.value,
    };
    if (props.wageType === 'home' && props.payDate) {
      payload.payDate = props.payDate;
    }

    const response = await api.value.importMigration(payload as any);
    if (!response.data?.success) {
      alert(`업로드 실패: ${response.data?.message || '알 수 없는 오류'}`);
      return;
    }

    const { savedCount = 0, registeredCount = 0, results = [] } = response.data.data || {};
    const failed = (results as any[]).filter((r) => !r.success);
    let msg = `${savedCount}건 저장 완료`;
    if (registeredCount) msg += ` (${registeredCount}명 사원 등록)`;
    if (failed.length) {
      msg += `\n\n실패 ${failed.length}건:\n${failed.map((f) => `${f.rowNum}행 ${f.name}: ${f.message}`).join('\n')}`;
    }
    alert(msg);
    emit('imported');
    close();
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`업로드 실패: ${message}${error.response?.status ? ` (${error.response.status})` : ''}`);
  } finally {
    importing.value = false;
    loading.value = false;
  }
};

const statusLabel = (status: string) => {
  if (status === 'ok') return '정상';
  if (status === 'missing_employee') return '미등록';
  return '오류';
};

const formatAmount = (row: MigrationPreviewRow) => {
  const p = row.preview || {};
  const val =
    props.wageType === 'home'
      ? Number(p.wageTotal) || 0
      : Number(p.cwhd_wage_total) || 0;
  if (!val) return '-';
  return val.toLocaleString();
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    "model-value": __props.modelValue,
    "max-width": "920",
    persistent: "",
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (emit('update:modelValue', $event)))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_v_icon, {
              size: "18",
              color: "white"
            }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                _createTextVNode("mdi-file-excel", -1)
              ]))]),
              _: 1
            })
          ]),
          _cache[4] || (_cache[4] = _createElementVNode("h2", { class: "header-title" }, "임금 엑셀 마이그레이션", -1)),
          _createElementVNode("span", _hoisted_4, _toDisplayString(wageTypeLabel.value), 1),
          _createElementVNode("button", {
            type: "button",
            class: "wage-excel-migration-dialog__close",
            disabled: loading.value,
            "aria-label": "닫기",
            onClick: close
          }, [
            _createVNode(_component_v_icon, { size: "18" }, {
              default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                _createTextVNode("mdi-close", -1)
              ]))]),
              _: 1
            })
          ], 8, _hoisted_5)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(LoadingOverlay, {
            show: loading.value,
            text: loadingText.value,
            variant: "default"
          }, null, 8, ["show", "text"]),
          _cache[11] || (_cache[11] = _createElementVNode("p", { class: "migration-intro" }, [
            _createTextVNode(" 타 프로그램에서 사용하던 임금 데이터를 엑셀로 가져옵니다. 업로드 시 "),
            _createElementVNode("strong", null, "4대보험·공제 고정 체크가 모두 ON"),
            _createTextVNode(" 상태로 저장되어 자동계산 없이 그대로 박제할 수 있습니다. ")
          ], -1)),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_v_btn, {
              variant: "flat",
              class: "btn-refresh",
              size: "small",
              disabled: loading.value,
              onClick: downloadTemplate
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { size: "14" }, {
                  default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                    _createTextVNode("mdi-download", -1)
                  ]))]),
                  _: 1
                }),
                _cache[6] || (_cache[6] = _createTextVNode(" 양식 다운로드 ", -1))
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createElementVNode("label", _hoisted_8, [
              _createElementVNode("input", {
                ref_key: "fileInputRef",
                ref: fileInputRef,
                type: "file",
                accept: ".xlsx,.xls",
                class: "migration-upload-input",
                disabled: loading.value,
                onChange: onFileSelected
              }, null, 40, _hoisted_9),
              _createVNode(_component_v_btn, {
                variant: "flat",
                class: "btn-search",
                size: "small",
                tag: "span",
                disabled: loading.value
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { size: "14" }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode("mdi-upload", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[8] || (_cache[8] = _createTextVNode(" 엑셀 선택 ", -1))
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            (selectedFileName.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(selectedFileName.value), 1))
              : _createCommentVNode("", true)
          ]),
          (summary.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createElementVNode("span", null, "총 " + _toDisplayString(summary.value.total) + "건", 1),
                _createElementVNode("span", _hoisted_12, "정상 " + _toDisplayString(summary.value.valid) + "건", 1),
                (summary.value.missingEmployees)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_13, "미등록 사원 " + _toDisplayString(summary.value.missingEmployees) + "건", 1))
                  : _createCommentVNode("", true),
                (summary.value.errors)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_14, "오류 " + _toDisplayString(summary.value.errors) + "건", 1))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (summary.value?.missingEmployees)
            ? (_openBlock(), _createElementBlock("label", _hoisted_15, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((registerMissingEmployees).value = $event)),
                  type: "checkbox"
                }, null, 512), [
                  [_vModelCheckbox, registerMissingEmployees.value]
                ]),
                _cache[9] || (_cache[9] = _createElementVNode("span", null, "미등록 사원 자동 등록 (생년월일 필수)", -1))
              ]))
            : _createCommentVNode("", true),
          (previewRows.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                _createElementVNode("table", _hoisted_17, [
                  _cache[10] || (_cache[10] = _createElementVNode("thead", null, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", null, "행"),
                      _createElementVNode("th", null, "성명"),
                      _createElementVNode("th", null, "생년월일"),
                      _createElementVNode("th", null, "직종"),
                      _createElementVNode("th", null, "과세합계"),
                      _createElementVNode("th", null, "상태"),
                      _createElementVNode("th", null, "메시지")
                    ])
                  ], -1)),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(previewRows.value, (row) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: row.rowNum,
                        class: _normalizeClass({
                  'row-error': row.status === 'error',
                  'row-warn': row.status === 'missing_employee',
                })
                      }, [
                        _createElementVNode("td", null, _toDisplayString(row.rowNum), 1),
                        _createElementVNode("td", null, _toDisplayString(row.name), 1),
                        _createElementVNode("td", null, _toDisplayString(row.birth || '-'), 1),
                        _createElementVNode("td", null, _toDisplayString(row.job || '-'), 1),
                        _createElementVNode("td", _hoisted_18, _toDisplayString(formatAmount(row)), 1),
                        _createElementVNode("td", null, [
                          _createElementVNode("span", {
                            class: _normalizeClass(["status-chip", `status-chip--${row.status}`])
                          }, _toDisplayString(statusLabel(row.status)), 3)
                        ]),
                        _createElementVNode("td", _hoisted_19, [
                          (row.errors?.length)
                            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(row.errors, (msg, i) => {
                                return (_openBlock(), _createElementBlock("div", { key: i }, _toDisplayString(msg), 1))
                              }), 128))
                            : (row.warnings?.length)
                              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(row.warnings, (msg, i) => {
                                  return (_openBlock(), _createElementBlock("div", { key: i }, _toDisplayString(msg), 1))
                                }), 128))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                  _createTextVNode("—")
                                ], 64))
                        ])
                      ], 2))
                    }), 128))
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_20, [
          _createVNode(_component_v_btn, {
            variant: "text",
            class: "btn-close",
            disabled: loading.value,
            onClick: close
          }, {
            default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
              _createTextVNode("닫기", -1)
            ]))]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_component_v_spacer),
          _createVNode(_component_v_btn, {
            variant: "flat",
            class: "btn-save",
            disabled: loading.value || !canImport.value,
            onClick: runImport
          }, {
            default: _withCtx(() => [
              (importing.value)
                ? (_openBlock(), _createBlock(_component_v_icon, {
                    key: 0,
                    size: "14",
                    class: "mdi-spin"
                  }, {
                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                      _createTextVNode("mdi-loading", -1)
                    ]))]),
                    _: 1
                  }))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(_toDisplayString(importButtonLabel.value), 1)
                  ], 64))
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})